import React, { useState, useMemo } from 'react';
import { 
  Grid, 
  TextField, 
  Autocomplete, 
  Chip, 
  Card, 
  CardContent, 
  Typography, 
  Box,
  FormHelperText,
  Alert,
  Stack,
  Button
} from '@mui/material';
import { 
  MusicNote, 
  People, 
  StickyNote2, 
  Add,
  Info
} from '@mui/icons-material';
import { BandRecommendationFormData } from '../../types/BandRecommendations';
import jazzSubgenres, { popularJazzSubgenres, jazzSubgenresGrouped } from '../../data/jazzSubgenres';

interface RecommendationDetailsProps {
  formData: BandRecommendationFormData;
  setFormData: React.Dispatch<React.SetStateAction<BandRecommendationFormData>>;
}

// 50/50 split — contemporary jazz, world, and cross-genre.
// Broad enough to be useful as reference points for a wide range of acts.
const femaleArtists = [
  'Esperanza Spalding',
  'Nubya Garcia',
  'Yazz Ahmed',
  'Mary Halvorson',
  'Arooj Aftab',
  'Shabaka Hutchings', // non-binary — kept as widely recognised reference
  'Tia Fuller',
  'Cécile McLorin Salvant',
  'Lakecia Benjamin',
  'Linda May Han Oh',
  'Brandee Younger',
  'Sofia Rei',
  'Somi',
  'Camille Thurman',
  'Anat Cohen',
];

const maleArtists = [
  'Kamasi Washington',
  'Robert Glasper',
  'Brad Mehldau',
  'Tigran Hamasyan',
  'Thundercat',
  'Christian Scott aTunde Adjuah',
  'Ambrose Akinmusire',
  'Flying Lotus',
  'Terence Blanchard',
  'Mulatu Astatke',
  'Ibrahim Maalouf',
  'Anouar Brahem',
  'Yussef Dayes',
  'Moses Sumney',
  'Joel Ross',
];

// Returns a randomised 50/50 selection of n total artists (n must be even).
// Called once per component mount using useMemo so the list stays stable
// while the user is on this step but refreshes if they leave and come back.
const getRandomSuggestions = (count: number): string[] => {
  const half = count / 2;

  const shuffled = <T,>(arr: T[]): T[] =>
    [...arr].sort(() => Math.random() - 0.5);

  return [
    ...shuffled(femaleArtists).slice(0, half),
    ...shuffled(maleArtists).slice(0, half),
  ].sort(() => Math.random() - 0.5); // shuffle combined so they're not gender-grouped
};

const allArtists = [...femaleArtists, ...maleArtists];

const RecommendationDetails: React.FC<RecommendationDetailsProps> = ({ formData, setFormData }) => {
  const [noteCharCount, setNoteCharCount] = useState(formData.recommendationNotes?.length || 0);

  // Stable per mount, 50/50, 6 suggestions shown at a time
  const suggestedPool = useMemo(() => getRandomSuggestions(6), []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'recommendationNotes') {
      setNoteCharCount(value.length);
    }
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleGenreChange = (event: React.SyntheticEvent, value: string[]) => {
    setFormData(prevData => ({
      ...prevData,
      genres: value.slice(0, 4)
    }));
  };

  const handleSimilarArtistsChange = (event: React.SyntheticEvent, value: string[]) => {
    const cleanedValue = value
      .map(artist => artist.trim())
      .filter((artist, index, arr) => 
        artist !== '' && arr.findIndex(a => a.toLowerCase() === artist.toLowerCase()) === index
      );
    setFormData(prevData => ({
      ...prevData,
      similarArtists: cleanedValue
    }));
  };

  const addQuickArtist = (artist: string) => {
    const currentArtists = formData.similarArtists || [];
    if (!currentArtists.some(a => a.toLowerCase() === artist.toLowerCase())) {
      handleSimilarArtistsChange(null as any, [...currentArtists, artist]);
    }
  };

  const getGenreHelperText = () => {
    const count = formData.genres?.length || 0;
    if (count === 0) return 'Select genres that best describe this artist\'s style';
    if (count === 4) return '✓ Maximum genres selected';
    return `${count}/4 genres selected`;
  };

  // Only show suggestions not already added, filtered from the stable random pool
  const getVisibleSuggestions = () => {
    const current = (formData.similarArtists || []).map(a => a.toLowerCase());
    return suggestedPool.filter(a => !current.includes(a.toLowerCase()));
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Genres Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MusicNote color="primary" />
            Musical Style & Genres
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select up to 4 jazz sub-genres that best represent this artist's style. These will be used beside your band to categorise.
          </Typography>
          
          <Autocomplete
            multiple
            id="genres"
            options={jazzSubgenresGrouped}
            groupBy={(option) => popularJazzSubgenres.includes(option) ? 'Popular' : 'All Genres'}
            value={formData.genres || []}
            onChange={handleGenreChange}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Jazz Sub-genres"
                fullWidth
                error={formData.genres?.length === 4}
                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
              />
            )}
            renderTags={(value: string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip 
                  variant="outlined" 
                  label={option} 
                  {...getTagProps({ index })} 
                  key={index}
                  color={index < 3 ? "primary" : "default"}
                />
              ))
            }
            limitTags={3}
            fullWidth
            sx={{ mt: 2 }}
          />
          <FormHelperText sx={{ mt: 1, color: formData.genres?.length === 4 ? 'primary.main' : 'text.secondary' }}>
            {getGenreHelperText()}
          </FormHelperText>
        </CardContent>
      </Card>

      {/* Similar Artists Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <People color="primary" />
            Similar Artists & Influences
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Add artists with a similar sound or style. This helps programmers understand 
            the act's reference points when making booking decisions.
          </Typography>
          
          <Autocomplete
            multiple
            freeSolo
            id="similarArtists"
            options={allArtists}
            value={formData.similarArtists || []}
            onChange={handleSimilarArtistsChange}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Similar Artists"
                placeholder="Type an artist name and press enter, or pick from suggestions below"
                fullWidth
                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
              />
            )}
            renderTags={(value: string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={index}
                  color="secondary"
                />
              ))
            }
            fullWidth
            sx={{ mt: 2 }}
          />
          
          {getVisibleSuggestions().length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Suggestions:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {getVisibleSuggestions().map((artist) => (
                  <Chip
                    key={artist}
                    label={artist}
                    variant="outlined"
                    size="small"
                    onClick={() => addQuickArtist(artist)}
                    icon={<Add fontSize="small" />}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Additional Notes Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StickyNote2 color="primary" />
            Additional Notes
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Anything else worth knowing about this act that isn't covered above — 
            live performance strengths, ideal venue or audience type, 
            recent activity, or anything that gives programmers useful context.
          </Typography>
          
          <TextField
            name="recommendationNotes"
            label="Additional Notes"
            multiline
            rows={4}
            value={formData.recommendationNotes || ''}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="e.g. Particularly strong live act. Best suited to intimate or mid-size venues. Recently completed a European tour. Available for festival slots from June onwards..."
            sx={{ mt: 2 }}
            inputProps={{ autoComplete: 'off' }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <FormHelperText>
              {noteCharCount === 0 
                ? 'Optional — but useful context for programmers' 
                : `${noteCharCount} characters`
              }
            </FormHelperText>
            
            {noteCharCount > 500 && (
              <Alert severity="info" sx={{ py: 0 }}>
                <Typography variant="caption">
                  Great detail — consider keeping it to the key points.
                </Typography>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RecommendationDetails;